/**
 * Helpers do hub consolidado.
 * Preferir GET /api/projects/{id}/ (cadastrais no root + blocos).
 * Alternativa: GET /api/projects/{id}/hub/ (cadastrais em `project`).
 */

import { emptyHubMap } from "./projectHubMapUtils";

export const PROJECT_HUB_ROLE_LABELS = {
  OWNER: "Proprietário",
  ADI_RESPONSIBLE: "Gestor",
  VERT_RESPONSIBLE: "Responsável VERT",
  VERT_TEAM: "Equipe VERT",
  OTHER_RESPONSIBLE: "Outro responsável",
  AREA_CREATED_BY: "Cadastrou a área",
};

/** Chaves dos blocos consolidados (não são campos cadastrais do projeto) */
const HUB_BLOCK_KEYS = new Set([
  "people",
  "geographic_coordinates",
  "map",
  "downloads",
  "reports",
  "summary",
  "guardian_areas",
  "alerts",
  "alert_requests",
  "firms_scans",
  "ema_stations",
  "carbon_credits",
  "project_carbon_credits",
  "environmental_engineering",
  "sdgs",
  "project",
]);

export function formatHubRoleLabels(roles = []) {
  return roles
    .map((role) => PROJECT_HUB_ROLE_LABELS[role] || role)
    .filter(Boolean)
    .join(" · ");
}

/**
 * Normaliza detalhe (`/projects/{id}/`) ou hub (`/hub/`) para um shape único:
 * { project, people, map, alerts, alert_requests, firms_scans, ... }
 */
export function normalizeProjectHubPayload(raw) {
  if (!raw || typeof raw !== "object") return null;

  const hasNestedProject =
    raw.project &&
    typeof raw.project === "object" &&
    !Array.isArray(raw.project) &&
    raw.project.id != null;

  // Formato /hub/: cadastrais em `project`
  if (hasNestedProject && raw.title == null) {
    return {
      project: raw.project,
      people: raw.people || null,
      geographic_coordinates: Array.isArray(raw.geographic_coordinates)
        ? raw.geographic_coordinates
        : [],
      map: raw.map || emptyHubMap(),
      downloads: Array.isArray(raw.downloads) ? raw.downloads : [],
      reports: Array.isArray(raw.reports) ? raw.reports : [],
      summary: raw.summary || {},
      guardian_areas: Array.isArray(raw.guardian_areas)
        ? raw.guardian_areas
        : [],
      alerts: Array.isArray(raw.alerts) ? raw.alerts : [],
      alert_requests: Array.isArray(raw.alert_requests)
        ? raw.alert_requests
        : [],
      firms_scans: Array.isArray(raw.firms_scans) ? raw.firms_scans : [],
      ema_stations: Array.isArray(raw.ema_stations) ? raw.ema_stations : [],
      carbon_credits: raw.carbon_credits ?? null,
      project_carbon_credits: Array.isArray(raw.project_carbon_credits)
        ? raw.project_carbon_credits
        : [],
      environmental_engineering: raw.environmental_engineering ?? null,
      sdgs: raw.sdgs ?? null,
    };
  }

  // Formato detalhe: cadastrais no root + blocos consolidados
  const project = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!HUB_BLOCK_KEYS.has(key)) {
      project[key] = value;
    }
  }

  return {
    project,
    people: raw.people || null,
    geographic_coordinates: Array.isArray(raw.geographic_coordinates)
      ? raw.geographic_coordinates
      : [],
    map: raw.map || emptyHubMap(),
    downloads: Array.isArray(raw.downloads) ? raw.downloads : [],
    reports: Array.isArray(raw.reports) ? raw.reports : [],
    summary: raw.summary || {},
    guardian_areas: Array.isArray(raw.guardian_areas) ? raw.guardian_areas : [],
    alerts: Array.isArray(raw.alerts) ? raw.alerts : [],
    alert_requests: Array.isArray(raw.alert_requests) ? raw.alert_requests : [],
    firms_scans: Array.isArray(raw.firms_scans) ? raw.firms_scans : [],
    ema_stations: Array.isArray(raw.ema_stations) ? raw.ema_stations : [],
    carbon_credits: raw.carbon_credits ?? null,
    project_carbon_credits: Array.isArray(raw.project_carbon_credits)
      ? raw.project_carbon_credits
      : [],
    environmental_engineering: raw.environmental_engineering ?? null,
    sdgs: raw.sdgs ?? null,
  };
}

/**
 * Flatten `guardian_areas` do hub para o shape usado pela tela Informações.
 */
export function flattenHubGuardianAreas(hubAreas = []) {
  return hubAreas.map((item) => {
    const area = item?.area && typeof item.area === "object" ? item.area : {};
    const resp = item?.responsibles || {};

    return {
      ...area,
      adi_responsible_name:
        resp.adi_responsible?.full_name || area.adi_responsible_name,
      vert_responsible_name:
        resp.vert_responsible?.full_name || area.vert_responsible_name,
      adi_responsible_contact: resp.adi_responsible || null,
      vert_responsible_contact: resp.vert_responsible || null,
      vert_team: Array.isArray(resp.vert_team) ? resp.vert_team : [],
      other_responsibles: Array.isArray(resp.other_responsibles)
        ? resp.other_responsibles
        : [],
      hub_downloads: item?.downloads || null,
      hub_reports: Array.isArray(item?.reports) ? item.reports : [],
      firms_areas: Array.isArray(item?.firms_areas) ? item.firms_areas : [],
    };
  });
}

/**
 * Mescla projeto da navegação com hub normalizado.
 */
export function mergeProjectWithHub(project, hub) {
  if (!hub) return project;
  const normalized =
    hub.project != null && (hub.people != null || hub.map != null || hub.alerts != null)
      ? hub
      : normalizeProjectHubPayload(hub) || hub;

  const hubProject =
    normalized.project && typeof normalized.project === "object"
      ? normalized.project
      : {};
  const hubAreas = flattenHubGuardianAreas(normalized.guardian_areas || []);

  return {
    ...project,
    ...hubProject,
    owner_details: normalized.people?.owner
      ? { ...(project?.owner_details || {}), ...normalized.people.owner }
      : project?.owner_details || hubProject.owner_details,
    guardian_areas:
      hubAreas.length > 0 ? hubAreas : project?.guardian_areas || [],
  };
}

export const HUB_SUMMARY_LABELS = {
  guardian_areas_count: "Áreas Guardian",
  firms_areas_count: "Áreas FIRMS",
  alerts_count: "Alertas",
  alert_requests_count: "Solicitações",
  firms_scans_count: "Scans FIRMS",
  ema_stations_count: "Estações EMA",
  reports_count: "Relatórios",
  downloads_count: "Downloads",
  team_count: "Equipe",
  map_points_count: "Pontos no mapa",
  project_carbon_credits_count: "Créditos",
};

export function getHubSummaryCards(summary = {}) {
  return Object.entries(HUB_SUMMARY_LABELS)
    .filter(([key]) => summary[key] != null)
    .map(([key, label]) => ({
      key,
      label,
      value: summary[key],
    }));
}

export function getHubDownloadTypeLabel(type) {
  const map = {
    project_image: "Imagem do projeto",
    adi_image: "Imagem ADI",
    project_kml: "KML da propriedade",
    firms_kml: "KML FIRMS",
    environmental_engineering: "Engenharia ambiental",
    alert_report_pdf: "Relatório PDF",
  };
  return map[type] || type || "Arquivo";
}

/** Categorias visuais da lista de downloads do hub */
export const HUB_DOWNLOAD_CATEGORIES = [
  { key: "image", label: "Imagens", badge: "Imagem" },
  { key: "kml", label: "KML", badge: "KML" },
  { key: "report", label: "Relatórios", badge: "PDF" },
  { key: "other", label: "Outros arquivos", badge: "Arquivo" },
];

const IMAGE_TYPES = new Set(["project_image", "adi_image"]);
const KML_TYPES = new Set(["project_kml", "firms_kml"]);
const REPORT_TYPES = new Set(["alert_report_pdf"]);

export function getHubDownloadCategory(type) {
  if (IMAGE_TYPES.has(type)) return "image";
  if (KML_TYPES.has(type)) return "kml";
  if (REPORT_TYPES.has(type)) return "report";
  if (typeof type === "string" && type.includes("kml")) return "kml";
  if (typeof type === "string" && (type.includes("image") || type.includes("img")))
    return "image";
  if (
    typeof type === "string" &&
    (type.includes("report") || type.includes("pdf"))
  )
    return "report";
  return "other";
}

/**
 * Agrupa downloads do hub + reports em Imagens / KML / Relatórios / Outros.
 * Cada item: { href, title, badge, key }
 */
export function groupHubDownloads(downloads = [], reports = []) {
  const groups = {
    image: [],
    kml: [],
    report: [],
    other: [],
  };

  downloads.forEach((item, index) => {
    if (!item?.url) return;
    const category = getHubDownloadCategory(item.type);
    const badge =
      HUB_DOWNLOAD_CATEGORIES.find((c) => c.key === category)?.badge ||
      "Baixar";
    groups[category].push({
      key: `dl-${item.type}-${index}`,
      href: item.url,
      title: item.label || getHubDownloadTypeLabel(item.type),
      badge,
    });
  });

  reports.forEach((report) => {
    const href = report.download_url || report.file_url;
    if (!href) return;
    const areaSuffix = report.guardian_area_name
      ? ` — ${report.guardian_area_name}`
      : "";
    groups.report.push({
      key: `report-${report.id || href}`,
      href,
      title: `${report.file_name || "Relatório PDF"}${areaSuffix}`,
      badge: "PDF",
    });
  });

  return HUB_DOWNLOAD_CATEGORIES.map((cat) => ({
    ...cat,
    items: groups[cat.key] || [],
  })).filter((cat) => cat.items.length > 0);
}

/**
 * Downloads por área (hub_downloads) organizados nas mesmas categorias.
 */
export function groupAreaHubDownloads(hubDownloads, areaName = "") {
  if (!hubDownloads || typeof hubDownloads !== "object") return [];

  const groups = {
    image: [],
    kml: [],
    report: [],
    other: [],
  };
  const areaLabel = areaName ? ` — ${areaName}` : "";

  if (hubDownloads.adi_image_url) {
    groups.image.push({
      key: `area-adi-image${areaLabel}`,
      href: hubDownloads.adi_image_url,
      title: `Imagem ADI${areaLabel}`,
      badge: "Imagem",
    });
  }

  if (hubDownloads.project_image_url) {
    groups.image.push({
      key: `area-project-image${areaLabel}`,
      href: hubDownloads.project_image_url,
      title: `Imagem do projeto${areaLabel}`,
      badge: "Imagem",
    });
  }

  if (hubDownloads.project_kml_url) {
    groups.kml.push({
      key: `area-project-kml${areaLabel}`,
      href: hubDownloads.project_kml_url,
      title: `KML propriedade${areaLabel}`,
      badge: "KML",
    });
  }

  const firmsUrls = Array.isArray(hubDownloads.firms_kml_urls)
    ? hubDownloads.firms_kml_urls
    : [];
  firmsUrls.forEach((url, i) => {
    if (!url) return;
    groups.kml.push({
      key: `area-firms-kml-${i}${areaLabel}`,
      href: url,
      title: `KML FIRMS${areaLabel}${firmsUrls.length > 1 ? ` (${i + 1})` : ""}`,
      badge: "KML",
    });
  });

  return HUB_DOWNLOAD_CATEGORIES.map((cat) => ({
    ...cat,
    items: groups[cat.key] || [],
  })).filter((cat) => cat.items.length > 0);
}
